import { CircularProgress, LinearProgress, Tab, Tabs as MUITabs } from "@mui/material";
import { useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router";
import { useReactToPrint } from "react-to-print";
import { toast } from "react-toastify";
import { io } from "socket.io-client";

import CAPDFContent from "./CAPDFContent";

import api from "../../BrandOnBoarding/api";
import Header from "../../Component/Header/Header";
import Modal from "../../Component/Modal/Modal";
import MUIButton from "../../Component/UI/MUIButton/MUIButton";
import { AuthContext } from "../../context/auth-context";
import { useDailyLimits } from "../../hooks/useDailyLimits";
import { getMetricsRecord } from "../../utils/constant";
import { updateValidatedSources } from "../../utils/helpers";
import { useDispatch, useSelector } from "react-redux";
import { fetchPersonas } from "../../redux/actions/personasActions";
import { fetchuserSet } from "../../redux/actions/userSentimentActions";

import { CategoryAnalysisContext } from "./category-analysis-context";
import CAForm from "./Form";
import CategoryDashboard from "./CategoryDashboard";
import useCAHistory from "./useCAHistory";
import userMetricSettingCreation from "../Set/userMetricSettingCreation";
import classes from "../BrandAudit/BrandAudit.module.css";

function ProgressIndicator({ total, completed }) {
  const progressPercentage = (completed / total) * 100;
  return (
    <div className={classes.progressIndicator}>
      <div className={classes.progressBar}>
        <div
          className={classes.progressFill}
          style={{ width: `${progressPercentage}%` }}
        />
      </div>
      <div className={classes.progressText}>
        {completed} of {total} tasks completed
      </div>
    </div>
  );
}

// signalStrength's "pending validation" sources can arrive as a plain URL string
// (old/legacy), a per-URL {url, count, http_status} object, or an already-merged
// {url: count, ...} map (v4's live socket push) — normalize each to a single-key
// {url: count} entry (or pass an already-merged map through as-is) so the reducer's
// merge-by-spread logic works regardless of which shape the backend sent.
function toOldSourcesEntry(entry) {
  if (typeof entry === "string") return { [entry]: 1 };
  if (entry && typeof entry === "object" && "url" in entry) {
    return { [entry.url]: entry.count ?? 1 };
  }
  return entry;
}

function CategoryAnalysisInner() {
  const [warningText, setWarningText] = useState("");
  const [cancelTransaction, setCancelTransaction] = useState(false);
  const [showLaunchDialog, setShowLaunchDialog] = useState(false);
  const [launchType, setLaunchType] = useState("test");
  const [isAuditRunning, setIsAuditRunning] = useState(false);
  const [processRetry, setProcessRetry] = useState(false);
  const [externalReportLoading, setExternalReportLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [caMode, setCaMode] = useState("baseline");
  const [isBaselineCAHtmlReportEnabled, setIsBaselineCAHtmlReportEnabled] = useState(false);

  const { user, setSelectedCustomerById } = useContext(AuthContext);
  const enableHTMLReport = caMode !== "baseline" || isBaselineCAHtmlReportEnabled;
  const caCtx = useContext(CategoryAnalysisContext);
  const caCtxRef = useRef(caCtx);
  const dispatch = useDispatch();
  const showUserMetricSettingModel = useSelector((store) => store.userSet.showModel);

  const transactionIdRef = useRef(null);
  const pollingRef = useRef(null);
  const socketRef = useRef(null);
  const coreAttributeMapRef = useRef({});
  const pdfRef = useRef(null);
  const metricsRecord = getMetricsRecord();

  const exportToPDF = useReactToPrint({
    contentRef: pdfRef,
    documentTitle: `${(caCtx.category?.length ? caCtx.category.join(", ") : caCtx.entity)} Category Analysis Overview`,
    removeAfterPrint: true,
    pageStyle: `
  @page {
    size: 380mm 430mm;
    margin-left: 10mm;
    margin-right: 10mm;
    margin-bottom: 5mm;
    margin-top: 15mm;
  }
  @media print {
    html, body {
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
      margin: 0 !important;
      padding: 0 !important;
      overflow: visible !important;
      width: 100% !important;
    }
    #print-pdf {
      padding: 0 !important;
      margin: 0 !important;
      width: 100% !important;
      overflow: visible !important;
      box-sizing: border-box !important;
    }
    * {
      overflow: visible !important;
      box-sizing: border-box !important;
      max-width: 100% !important;
      word-wrap: break-word !important;
      overflow-wrap: break-word !important;
    }
    .no-print {
      display: none !important;
    }
    .ca-print-only {
      display: block !important;
    }
    .Toastify,
    .Toastify__toast-container,
    .Toastify__toast {
      display: none !important;
    }
    .MuiModal-root,
    .MuiModal-backdrop,
    .MuiDrawer-root,
    .MuiPopover-root,
    .MuiPopper-root,
    .MuiTooltip-popper,
    .MuiSnackbar-root,
    [role="tooltip"] {
      display: none !important;
    }
    .pdf-sources-section {
      overflow: hidden !important;
      max-height: 450px !important;
      break-inside: avoid !important;
      page-break-inside: avoid !important;
    }
    /* CA PDF sources: remove height cap and override ReTableView's
       inline max-height:500px / overflowY:auto scroll container so all
       rows are visible in the exported PDF. */
    .ca-pdf-sources-wrapper,
    .ca-pdf-sources-wrapper * {
      max-height: none !important;
      overflow: visible !important;
    }
    /* Each CA PDF section card: if it doesn't fit on the remaining page
       space, move the whole card to the next page instead of splitting it. */
    .ca-pdf-section {
      break-inside: avoid !important;
      page-break-inside: avoid !important;
    }
    .section {
      break-inside: avoid !important;
      page-break-inside: avoid !important;
    }
    .section_summary {
      break-inside: avoid !important;
      page-break-inside: avoid !important;
    }
    .discovery-ui-table          { display: none  !important; }
    .discovery-print-table       { display: block !important; }
    .discovery-print-competitors { display: block !important; }
  }
`,
  });

  const webSocketBaseURL = process.env.REACT_APP_ORCHESTRATOR_BASE_URL;
  const location = useLocation();
  const showLaunchMessage = localStorage.getItem("showLaunchMessage");

  const fullCALimits = useDailyLimits("CategoryAnalysis");
  const baselineCALimits = useDailyLimits("BaselineCategoryAnalysis");

  const activeLimits = caMode === "baseline" ? baselineCALimits : fullCALimits;

  const {
    launchText,
    hoverText,
    updateLaunches,
    launchesRemaining,
    isSpecial,
    frequency: launchLimitFrequency,
    updatePersonasOnly,
  } = activeLimits;

  const isBaselineCategoryAnalysisEnabled = fullCALimits.isBaselineCategoryAnalysisEnabled;

  const { getHistory } = useCAHistory();
  const testLLMModel = process.env.REACT_APP_LOWCOST_MODEL;

  // Keep context ref in sync
  useEffect(() => {
    caCtxRef.current = caCtx;
  }, [caCtx]);
  // Sync transactionId ref
  useEffect(() => {
    if (caCtx.state.id) return;
    setCaMode(isBaselineCategoryAnalysisEnabled ? "baseline" : "full");
  }, [isBaselineCategoryAnalysisEnabled]);

  useEffect(() => {
    if (caCtx.state.transactionId) {
      transactionIdRef.current = caCtx.state.transactionId;
    } else {
      transactionIdRef.current = null;
    }
  }, [caCtx.state.transactionId]);

  // Fetch personas, templates, and history on mount
  useEffect(() => {
    const customerId = user?.customer_id;
    const userId = user?.user_role?.user_id;
    if (customerId) {
      dispatch(fetchPersonas(customerId));
      if (userId) dispatch(fetchuserSet({ customer_id: customerId, user_id: userId }));
    }
    getHistory(1, 5);
  }, []);

  // Load CA record from URL params
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const id = searchParams.get("id");
    const type = searchParams.get("type");
    const user_id = searchParams.get("user_id");
    const urlCustomerId = searchParams.get("customer_id");
    if (urlCustomerId && String(user?.user_role?.user_id) === String(user_id)) {
      setSelectedCustomerById(Number(urlCustomerId));
    }
    if (id && type && user_id && !cancelTransaction) {
      // Deep-linked from Home Page - New's "View category analysis" — show a
      // loading state over this tab until the specific record arrives.
      setExternalReportLoading(true);
      loadCategoryAnalysis(id, type, user_id, false);
    }
    if (caCtx.abort_launch) {
      // nothing to abort for sockets
    }
  }, [caCtx.abort_launch]);

  // Set up socket once
  useEffect(() => {
    if (!socketRef.current) {
      const isProd = process.env.REACT_APP_ENV === "production";
      const socketOptions = { reconnectionAttempts: 5 };
      const socketUrl = process.env.REACT_APP_SOCKET_BASE_URL;
      if (isProd) {
        socketOptions.path = "/ows/";
      } else {
        socketOptions.transports = ["websocket"];
      }
      socketRef.current = io(socketUrl, socketOptions);
      socketRef.current.on("connect", () => console.log("CA socket connected:", socketRef.current.id));
      socketRef.current.on("disconnect", () => console.warn("CA socket disconnected"));
      socketRef.current.on("live-data", handleSocketData);
    }

    const handleOnline = () => {
      if (socketRef.current && !socketRef.current.connected && !socketRef.current.active) {
        socketRef.current.connect();
      }
    };
    window.addEventListener("online", handleOnline);

    return () => {
      window.removeEventListener("online", handleOnline);
      transactionIdRef.current = null;
    };
  }, []);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, []);

  // --- Helpers ---
  const onlyIfValidated = (data, showOldSourcesRecord, isValidated, empty) => {
    const emptyDefault = empty !== undefined ? empty : (Array.isArray(data) ? [] : {});
    if (showOldSourcesRecord) {
      if (Array.isArray(data)) return data;
      if (data == null) return [];
      return [data];
    }
    return isValidated ? data : emptyDefault;
  };

  // --- Socket handler (same logic as BrandAudit) ---
  const handleSocketData = (data) => {
    if (!data) return;

    let transactionId, type, request, response, category, llm, market, persona,
      transaction, completedTaskCount, coreAttributeData, isValidated, commonReq;

    if (Array.isArray(data?.coreAttributeData)) {
      if (!data?.coreAttributeData[0]) return;
      transactionId = data?.coreAttributeData[0]?.transaction?.transaction_id;
      type = data?.coreAttributeData[0]?.type;
      coreAttributeData = data?.coreAttributeData;
      completedTaskCount = data?.completedTaskCount;
      isValidated = data?.isValidated;
      commonReq = {
        category: data?.coreAttributeData[0]?.category ?? "",
        llm: data?.coreAttributeData[0]?.llm ?? "",
        market: data?.coreAttributeData[0]?.market ?? "",
        persona: data?.coreAttributeData[0]?.persona ?? "",
      };
    } else {
      ({ type, request, response, category, llm, market, persona, transaction, completedTaskCount, isValidated } = data);
      transactionId = transaction?.transaction_id ?? data?.transactionId;
      commonReq = { category: category ?? "", llm: llm ?? "", market: market ?? "", persona: persona ?? "" };
    }

    const currentContextTxnId = transactionIdRef.current;
    if (transactionId !== currentContextTxnId) return;

    const showOldSourcesRecord = data?.SourceValidatorStatus === "PENDING";
    const ctx = caCtxRef.current;

    if (data?.status === "Transaction Successfull" || data?.status === "Transaction failed") {
      loadCategoryAnalysis(transactionId, "brand", user?.user_role?.user_id, true);
      ctx.updateTransactionStatus(data?.status === "Transaction Successfull" ? "COMPLETED" : "RETRY");
      if (transactionId === ctx.state.finalTransactionId) {
        ctx.updateFinalTransactionStatus("COMPLETED");
      }
    }
    if (data?.sourceValidationStatus) {
      loadCategoryAnalysis(transactionId, "brand", user?.user_role?.user_id, true);
      ctx.updateSourceValidationStatus(data.sourceValidationStatus);
    }

    ctx.updateStatus({ completedTaskCount });

    switch (type) {
      case "rank":
        if (!response) return;
        response = { ...response, request_id: data.id, isValidated };
        ctx.updateRankScores({ req: commonReq, res: response });
        ctx.updateSources({
          res: onlyIfValidated(response?.sources?.rank || [], showOldSourcesRecord, isValidated, []),
          req: commonReq,
          showOldSourcesRecord,
          type: metricsRecord[type],
        });
        break;

      case "compositeRank": {
        if (!response) return;
        const { category: cRankCat, market: cRankMkt, persona: cRankPersona } = commonReq;
        ctx.updateCompositeRankScores({
          req: { category: cRankCat, market: cRankMkt, persona: cRankPersona },
          res: response,
        });
        break;
      }

      case "sentiment": {
        if (!response || !transaction || !request?.category) return;
        const entityData = response?.data?.[0];
        const sentimentSources = onlyIfValidated(response?.sources?.sentiment || [], showOldSourcesRecord, isValidated, []);
        const updatedExtras = !showOldSourcesRecord ? updateValidatedSources(response.extras) : response.extras;
        const brandRes = response?.error
          ? { error: "something went wrong" }
          : {
              name: entityData?.name || "Unknown",
              favorability: entityData?.score ?? 0,
              extras: updatedExtras,
              sentimentSources,
              isValidated,
              rbiType: response?.rbiType,
            };
        const competitors = caCtxRef.current?.competitors?.[request.category] || [];
        let index = competitors.findIndex((item) => item.name?.toLowerCase() === entityData?.name?.toLowerCase());
        if (transaction?.brand === entityData?.name) index = -1;
        ctx.updateBrandScores({ req: commonReq, res: { data: brandRes, index }, sentimentSources, isValidated });
        ctx.updateSources({ res: sentimentSources, req: commonReq, showOldSourcesRecord, type: metricsRecord[type] });
        break;
      }

      case "signalStrength": {
        if (!response) return;
        const negativeAttributes = response?.aggregatedNegatives || [];
        const positiveAttributes = response?.aggregatedPositives || [];
        let sourcesPositiveArray = onlyIfValidated(response?.aggregatedPositiveSources || [], showOldSourcesRecord, isValidated, []);
        let sourcesNegativeArray = onlyIfValidated(response?.aggregatedNegativeSources || [], showOldSourcesRecord, isValidated, []);
        if (showOldSourcesRecord) {
          sourcesPositiveArray = sourcesPositiveArray.map(toOldSourcesEntry);
          sourcesNegativeArray = sourcesNegativeArray.map(toOldSourcesEntry);
        }
        ctx.updatePositiveNegativeAttributes({
          res: {
            name: request?.brand,
            positives: positiveAttributes,
            negatives: negativeAttributes,
            baseResponses: response?.baseResponses || {},
          },
          req: commonReq,
        });
        ctx.updateSources({ res: sourcesPositiveArray, req: commonReq, showOldSourcesRecord, type: "Positive Attributes" });
        ctx.updateSources({ res: sourcesNegativeArray, req: commonReq, showOldSourcesRecord, type: "Negative Attributes" });
        break;
      }

      case "categoryDimension": {
        if (!response) return;
        const normalize = (src) =>
          Array.isArray(src)
            ? onlyIfValidated(src, showOldSourcesRecord, isValidated, [])
            : onlyIfValidated(src ? [src] : [], showOldSourcesRecord, isValidated, []);
        const sourcesCategory = normalize(response?.sources?.category);
        const sourcesDim = normalize(response?.sources?.dimension);
        ctx.updateCategoryDimensions({
          ...commonReq,
          data: response?.data || [],
          summary: response?.summary || "",
          sources: sourcesCategory,
          baseResponses: { ...(response?.baseResponses || {}), multi_prompting: response?.multi_prompting || false, num_runs: response?.num_runs },
          isValidated,
        });
        ctx.updateSources({ res: [...sourcesCategory, ...sourcesDim], req: commonReq, showOldSourcesRecord, type: metricsRecord[type] });
        break;
      }

      case "core-attribute": {
        if (!Array.isArray(coreAttributeData)) return;
        const expectedCount =
          caCtxRef.current?.competitors?.[coreAttributeData?.[0]?.category]?.length ?? 0;

        if (coreAttributeData?.length === expectedCount) {
          const grouped = coreAttributeData.reduce((acc, item) => {
            const key = `${item.category}__${item.llm}__${item.market}_${item.persona}`;
            if (!acc[key]) acc[key] = [];
            acc[key].push(item);
            return acc;
          }, {});

          Object.entries(grouped).forEach(([key, items]) => {
            const [groupCategory] = key.split("__");
            const resData = items.filter((r) => r?.response).map((r) => r.response);
            let coreAttributeSummary = "";
            let category_averages = resData[0]?.category_averages || {};
            const sources = [];
            const products = [];

            for (const res of resData) {
              if (!res?.error) {
                coreAttributeSummary += res.products?.[0]?.summary || "";
                coreAttributeSummary += "\n\n";
                const productSources = onlyIfValidated(res?.sources?.product || [], showOldSourcesRecord, isValidated, []);
                products.push({ ...res.products?.[0], brandName: res.brandName, sources: productSources, baseResponses: res.baseResponses || null, multi_prompting: res.multi_prompting || false, num_runs: res.num_runs });
                if (showOldSourcesRecord || isValidated) {
                  const norm = (src) => (Array.isArray(src) ? src : src ? [src] : []);
                  sources.push(...norm(res?.sources?.category), ...norm(res?.sources?.product));
                }
              }
            }

            const competitorsList = caCtxRef.current?.competitors?.[groupCategory] || [];
            const entities = competitorsList.map((item) => item.name);

            const sortedProducts = [];
            const baseResponsesArray = [];
            entities.forEach((company) => {
              const normalizedCompany = company.toLowerCase().replace(/\s+/g, "");
              for (const product of products) {
                const normalizedProductName = (product?.brandName || product?.name || "").toLowerCase().replace(/\s+/g, "");
                if (normalizedProductName === normalizedCompany) {
                  if (product.baseResponses?.baseResponses && Array.isArray(product.baseResponses.baseResponses)) {
                    const firstItem = product.baseResponses.baseResponses[0];
                    const isPerAttributeMulti = firstItem?.attribute != null && Array.isArray(firstItem?.runs);
                    const isFlatRunMulti = firstItem?.run != null && firstItem?.baseRes != null && product.baseResponses.baseResponses.length > 1;
                    const isMultiPrompting = product.multi_prompting === true || isPerAttributeMulti || isFlatRunMulti;
                    baseResponsesArray.push({
                      brandName: product.brandName,
                      basePrompt: product.baseResponses.basePrompt || "",
                      multi_prompting: isMultiPrompting,
                      num_runs: product.num_runs || (isPerAttributeMulti ? firstItem?.runs?.length : undefined),
                      baseResponse: isPerAttributeMulti
                        ? product.baseResponses.baseResponses
                        : isFlatRunMulti
                          ? product.baseResponses.baseResponses
                          : product.baseResponses.baseResponses.map((item) => ({ baseRes: item.baseRes || "" })),
                    });
                  }
                  const { baseResponses: _br, ...productWithout } = product;
                  sortedProducts.push(productWithout);
                  break;
                }
              }
            });

            ctx.updateCoreAttributes({
              ...commonReq,
              category_averages,
              products: sortedProducts,
              summary: coreAttributeSummary,
              baseResponses: baseResponsesArray,
              isValidated,
            });
            ctx.updateSources({ res: sources, req: commonReq, showOldSourcesRecord, type: metricsRecord[type] });
          });
        }
        break;
      }

      default:
        break;
    }
  };

  // --- Load from polling or history ---
  function loadSocketData(socketResponse, requestObj, currentTransactionId, showOldSourcesRecord) {
    if (!socketResponse || typeof socketResponse !== "object") return;
    const { type, request, response, category, llm, market, persona, status, transactionId, isValidated } = socketResponse;
    if (!type || !request) return;
    if (status !== "COMPLETED") return false;
    if (parseInt(transactionId) !== parseInt(currentTransactionId)) return;

    const ctx = caCtxRef.current;
    const commonReq = { category: category ?? "", llm: llm ?? "", market: market ?? "", persona: persona ?? "" };

    const onlyIfVal = (data, empty = Array.isArray(data) ? [] : {}) => {
      if (showOldSourcesRecord) { if (Array.isArray(data)) return data; if (data == null) return []; return [data]; }
      return isValidated ? data : empty;
    };
    switch (type) {
      case "rank":
        if (!response) return;
        ctx.updateRankScores({ req: commonReq, res: { ...response, request_id: socketResponse.id, isValidated } });
        ctx.updateSources({ res: onlyIfVal(response?.sources?.rank || [], []), req: commonReq, showOldSourcesRecord, type: metricsRecord[type] });
        break;
      case "compositeRank": {
        if (!response) return;
        ctx.updateCompositeRankScores({
          req: { category: category ?? "", market: market ?? "", persona: persona ?? "" },
          res: response,
        });
        break;
      }
      case "sentiment": {
        if (!response || !request?.category) return;
        const entityData = response?.data?.[0];
        const sentimentSources = onlyIfVal(response?.sources?.sentiment || [], []);
        const updatedExtras = !showOldSourcesRecord ? updateValidatedSources(response.extras) : response.extras;
        const brandRes = response?.error ? { error: "something went wrong" } : { name: entityData?.name || "Unknown", favorability: entityData?.score ?? 0, extras: updatedExtras, sentimentSources, isValidated, rbiType: response?.rbiType };
        const competitors = requestObj?.competitor?.[request.category] || ctx?.competitors?.[request.category] || [];
        let index = competitors.findIndex((item) => item.name?.toLowerCase() === entityData?.name?.toLowerCase());
        if (requestObj?.brand === entityData?.name) index = -1;
        ctx.updateBrandScores({ req: commonReq, res: { data: brandRes, index }, sentimentSources, isValidated });
        ctx.updateSources({ res: sentimentSources, req: commonReq, showOldSourcesRecord, type: metricsRecord[type] });
        break;
      }
      case "signalStrength": {
        if (!response) return;
        const pA = onlyIfVal(response?.aggregatedPositiveSources || [], []);
        const nA = onlyIfVal(response?.aggregatedNegativeSources || [], []);
        ctx.updatePositiveNegativeAttributes({
          res: {
            name: request?.brand,
            positives: response?.aggregatedPositives || [],
            negatives: response?.aggregatedNegatives || [],
            baseResponses: response?.baseResponses || {},
          },
          req: commonReq,
        });
        ctx.updateSources({ res: showOldSourcesRecord ? pA.map(toOldSourcesEntry) : pA, req: commonReq, showOldSourcesRecord, type: "Positive Attributes" });
        ctx.updateSources({ res: showOldSourcesRecord ? nA.map(toOldSourcesEntry) : nA, req: commonReq, showOldSourcesRecord, type: "Negative Attributes" });
        break;
      }
      case "categoryDimension": {
        if (!response) return;
        const norm = (src) => Array.isArray(src) ? onlyIfVal(src, []) : onlyIfVal(src ? [src] : [], []);
        const srcCat = norm(response?.sources?.category);
        const srcDim = norm(response?.sources?.dimension);
        ctx.updateCategoryDimensions({ ...commonReq, data: response?.data || [], summary: response?.summary || "", sources: srcCat, baseResponses: { ...(response?.baseResponses || {}), multi_prompting: response?.multi_prompting || false, num_runs: response?.num_runs }, isValidated });
        ctx.updateSources({ res: [...srcCat, ...srcDim], req: commonReq, showOldSourcesRecord, type: metricsRecord[type] });
        break;
      }
      case "core-attribute": {
        if (!response || !category || !llm) return;
        const key = `${llm}__${category}_${persona}`;
        if (!coreAttributeMapRef.current[key]) coreAttributeMapRef.current[key] = [];

        const newProductName = (response?.brandName || response?.products?.[0]?.name || "")?.toLowerCase().trim();
        const alreadyExists = coreAttributeMapRef.current[key].some(
          (item) => (item?.brandName || item?.products?.[0]?.name || "")?.toLowerCase().trim() === newProductName
        );
        if (!alreadyExists && newProductName) coreAttributeMapRef.current[key].push(response);

        const expectedCount = requestObj?.competitor?.[category]?.length ?? 0;
        if (coreAttributeMapRef.current[key].length === expectedCount) {
          const group = coreAttributeMapRef.current[key];
          let coreAttributeSummary = "";
          const products = [];
          const sources = [];
          const category_averages = group[0]?.category_averages;

          for (const res of group) {
            if (!res?.error && Array.isArray(res?.products)) {
              coreAttributeSummary += res.products?.[0]?.summary || "";
              coreAttributeSummary += "\n\n";
              const productSources = onlyIfVal(res?.sources?.product || [], []);
              products.push({ ...res.products?.[0], brandName: res.brandName, sources: productSources, baseResponses: res.baseResponses || null, multi_prompting: res.multi_prompting || false, num_runs: res.num_runs });
              if (showOldSourcesRecord || isValidated) {
                const normSrc = (src) => Array.isArray(src) ? src : src ? [src] : [];
                sources.push(...normSrc(res?.sources?.category), ...normSrc(res?.sources?.product));
              }
            }
          }

          const entities = requestObj?.competitor?.[category]?.map((item) => item.name) ?? [];
          const sortedProducts = [];
          const baseResponsesArray = [];
          for (const company of entities) {
            const normalizedCompany = company.toLowerCase().replace(/\s+/g, "");
            for (const product of products) {
              const normalizedProductName = (product?.brandName || product?.name || "")?.toLowerCase()?.replace(/\s+/g, "");
              if (normalizedProductName === normalizedCompany) {
                if (product.baseResponses?.baseResponses && Array.isArray(product.baseResponses.baseResponses)) {
                  const firstItem = product.baseResponses.baseResponses[0];
                  const isPerAttributeMulti = firstItem?.attribute != null && Array.isArray(firstItem?.runs);
                  const isFlatRunMulti = firstItem?.run != null && firstItem?.baseRes != null && product.baseResponses.baseResponses.length > 1;
                  const isMultiPrompting = product.multi_prompting === true || isPerAttributeMulti || isFlatRunMulti;
                  baseResponsesArray.push({
                    brandName: product.brandName,
                    basePrompt: product.baseResponses.basePrompt || "",
                    multi_prompting: isMultiPrompting,
                    num_runs: product.num_runs || (isPerAttributeMulti ? firstItem?.runs?.length : undefined),
                    baseResponse: isPerAttributeMulti
                      ? product.baseResponses.baseResponses
                      : isFlatRunMulti
                        ? product.baseResponses.baseResponses
                        : product.baseResponses.baseResponses.map((item) => ({ baseRes: item.baseRes || "" })),
                  });
                }
                const { baseResponses: _br, ...productWithout } = product;
                sortedProducts.push(productWithout);
                break;
              }
            }
          }

          ctx.updateCoreAttributes({ category_averages, products: sortedProducts, summary: coreAttributeSummary, baseResponses: baseResponsesArray, ...commonReq, isValidated });
          ctx.updateSources({ res: sources, req: commonReq, showOldSourcesRecord, type: metricsRecord[type] });
          delete coreAttributeMapRef.current[key];
        }
        break;
      }
      default:
        break;
    }
  }

  async function loadCategoryAnalysis(id, type, user_id, refreshRecord = false, isTabChange = false, isUpdateFormData = true) {
    try {
      const response = await api.get(`${webSocketBaseURL}/transaction/categoryAnalysis/${user_id}/${id}`);
      if (response?.data?.transactionStatus !== "PENDING") {
        const transactionId = parseInt(id);
        const requestObj = response?.data?.task?.[0]?.request_object;

        if (refreshRecord) {
          coreAttributeMapRef.current = {};
          caCtxRef.current.refreshState();
        } else {
          if (requestObj) {
            const completedTests = response?.data?.testIds.filter((t) => t.status !== "INACTIVE");
            const transactionIdIndex = completedTests.findIndex((t) => t.transactionId === transactionId);
            setActiveTab(transactionIdIndex);
            caCtxRef.current.resetState();
            response?.data?.testIds
              .map((item, originalIndex) => ({ ...item, originalIndex }))
              .filter((item) => item.status !== "INACTIVE")
              .forEach((item) => {
                if (response?.data?.finalTransactionId === item.transactionId) {
                  caCtxRef.current.updateLaunchRecord({ name: "Final Analysis", transactionId: item.transactionId });
                } else {
                  caCtxRef.current.updateLaunchRecord({ name: `Test Analysis ${item.originalIndex + 1}`, transactionId: item.transactionId, totalTestAudit: response?.data?.testIds?.length });
                }
              });
            caCtxRef.current.updateFinalTransactionId(response?.data?.finalTransactionId);
            caCtxRef.current.updateFinalTransactionStatus(response?.data?.finalTransactionIdStatus);
          }
        }

        const { transactionStatus, completedTaskCount, totalCount, userMetricSettingId, sourceValidationStatus, brandAuditLimits, is_shared, shared_at, owner_user_id } = response?.data || {};

        if (transactionStatus === "RUNNING" && isTabChange) {
          startPolling(transactionId, type);
          caCtxRef.current.updateLoading(true);
          setIsAuditRunning(true);
        }

        caCtxRef.current.updateLaunch(brandAuditLimits);
        caCtxRef.current.updateType("brand");
        caCtxRef.current.updateTransactionId(id);
        caCtxRef.current.updateId(id);
        caCtxRef.current.updatedLaunched(true);
        caCtxRef.current.updateTransactionStatus(transactionStatus);
        caCtxRef.current.updateSourceValidationStatus(sourceValidationStatus);
        caCtxRef.current.updateShareInfo({ is_shared, shared_at, owner_user_id });
        caCtxRef.current.updateCustomTemplateID(userMetricSettingId || null);
        caCtxRef.current.setMetricSettingObj(response?.data?.user_metric_setting_object);

        if ((["COMPLETED", "RETRY"].includes(transactionStatus) || (totalCount > 0 && completedTaskCount === totalCount)) && !isTabChange) {
          getHistory(1, 5);
          setProcessRetry(false);
          stopPolling();
          caCtxRef.current.updateLoading(false);
          setIsAuditRunning(false);
          caCtxRef.current.updateLaunchTransaction(false);
        } else if (!isTabChange) {
          caCtxRef.current.updateLoading(true);
          setIsAuditRunning(true);
        }

        caCtxRef.current.updateStatus({
          totalCount: requestObj ? totalCount : (totalCount || undefined),
          completedTaskCount,
        });

        if (!requestObj) return;

        // For CA, brand is empty string; use first category as entity label
        const entityLabel = requestObj.category?.[0] || "";
        let competitors = { ...requestObj?.competitor };

        setCaMode(requestObj.caMode || "full");
        setIsBaselineCAHtmlReportEnabled(requestObj.is_baseline_html_reporting_enabled || false);
        caCtxRef.current.updatePrimaryLLM(requestObj.primaryLLM);
        caCtxRef.current.updateConversationEnabled(false);
        caCtxRef.current.updatedCompetitors(competitors);
        caCtxRef.current.updateEntity(entityLabel);
        caCtxRef.current.updateMarket(requestObj.market);
        caCtxRef.current.updatePersona(
          requestObj?.persona?.length > 0
            ? requestObj.persona
            : [{ title: "General Persona", description: "General Persona" }]
        );
        caCtxRef.current.updateCategory(requestObj.category);
        caCtxRef.current.updatePersonaSettings({
          applyPersonaToAllCategory: requestObj?.applyPersonaToAllCategory ?? false,
          applyPersonaToAllMarkets: requestObj?.applyPersonaToAllMarkets ?? false,
        });
        caCtxRef.current.updateSelectedLLM(requestObj.primaryLLM);
        caCtxRef.current.updateLLM(requestObj.llm);
        caCtxRef.current.updateIsBatchMode(
          !!requestObj?.llmBatchConfig && Object.keys(requestObj.llmBatchConfig).length > 0
        );
        caCtxRef.current.updateFormData(isUpdateFormData);
        caCtxRef.current.updateSelectedMarket(requestObj.market[0]);
        caCtxRef.current.updateSelectedCategory(requestObj.category[0]);
        caCtxRef.current.updateSelectedPersona(
          Array.isArray(requestObj?.persona) && requestObj.persona.length > 0
            ? requestObj.persona[0]?.title ?? requestObj.persona[0]
            : "General Persona"
        );
        caCtxRef.current.updateName(`${entityLabel} Category Analysis Overview`);
        caCtxRef.current.updateRbiType(requestObj.rbiType || "propensityToRecommend");
        if (requestObj.keywords) caCtxRef.current.updateKeywords(requestObj.keywords);
        if (requestObj.selectedMetrics) caCtxRef.current.updateSelectedMetrics(requestObj.selectedMetrics);
        caCtxRef.current.updateRankCompetitiveSet(requestObj.rank_competitive_set || []);
        caCtxRef.current.updateRankMethodology(requestObj.rank_methodology || "by_category");
        caCtxRef.current.cancelSelection();

        const showOldSourcesRecord = sourceValidationStatus === "PENDING";
        for (const record of response?.data.task) {
          loadSocketData(record, requestObj, id, showOldSourcesRecord);
        }
      }
    } catch (error) {
      console.error("loadCategoryAnalysis error:", error);
      if (error.message === "Network Error") {
        toast.warn("Please check your network connection");
      } else {
        toast.error("Failed to load Category Analysis report.");
      }
    } finally {
      setExternalReportLoading(false);
    }
  }

  const startPolling = (transactionId, type) => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    pollingRef.current = setInterval(() => {
      loadCategoryAnalysis(transactionId, type, user?.user_role?.user_id, true);
    }, 5 * 60 * 1000);
  };

  const stopPolling = () => {
    if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
  };

  async function retryCategoryAnalysis() {
    try {
      const transactionId = caCtx.state.transactionId;
      if (!transactionId) throw new Error("Invalid record: transactionId not found");
      getHistory();
      setProcessRetry(true);
      caCtx.updateTransactionStatus("RUNNING");
      await fetch(`${webSocketBaseURL}/retry-transaction-request/${transactionId}`);
    } catch (error) {
      toast.error("Failed to retry Category Analysis");
      setProcessRetry(false);
    }
  }
console.log("caCtx",caCtx)
  const handleTabChange = (_event, selectedTab) => {
    stopPolling();
    const data = caCtx?.state?.launchRecord[selectedTab];
    setActiveTab(selectedTab);
    loadCategoryAnalysis(data?.transactionId, "brand", user?.user_role?.user_id, true, true);
    window.dispatchEvent(new Event("resize"));
    window.dispatchEvent(new Event("tabchange"));
  };

  const categoryAnalysisCall = async (values) => {
    const marketArray =
      Array.isArray(values.market) && values.market.length > 0 ? values.market : ["General Market"];
    const personaList =
      Array.isArray(values.persona) && values.persona.length > 0
        ? values.persona.map((p) => {
            if (typeof p === "string") return { title: p, description: p };
            if (typeof p === "object" && p?.title) return p;
            return null;
          }).filter(Boolean)
        : [{ title: "General Persona", description: "General Persona" }];

    const competitorsData = { ...values.competitorsData };
    if (competitorsData.hasOwnProperty("default")) delete competitorsData.default;

    const payload = {
      user_id: user?.user_role?.user_id,
      brand: "",
      brandDomainName: "",
      market: marketArray,
      type: "brand",
      llm: values.llm,
      category: values.category,
      persona: personaList,
      primaryMarket: values.market[0],
      primaryLLM: values.primaryLLM,
      competitor: competitorsData,
      rbiType: values.rbiType,
      request_type: "categoryAnalysis",
      caMode: values.caMode || "full",
      is_baseline_html_reporting_enabled: values.caMode === "baseline" ? false : undefined,
      shouldProcessImmediately: "true",
      isMock: false,
      user_metric_setting_id: values.metricSettingId,
      customerId: user?.customer_id || 0,
      run_type: launchType,
      final_transaction_id: caCtx.state.finalTransactionId || null,
      categoryWiseMetric: values.categoryWiseMetric,
      conversation_enabled: false,
      keywords: values.keywords,
      selectedMetrics: values.selectedMetrics,
      ...(values.isBatchMode && Array.isArray(values?.llm) && values.llm.length
        ? { llmBatchConfig: Object.fromEntries(values.llm.map((l) => [l, true])) }
        : {}),
      ...(values.rankCompetitiveSet?.length
        ? { rank_competitive_set: values.rankCompetitiveSet }
        : {}),
      ...(user?.is_custom_rank
        ? { rank_methodology: values.rankMethodology || "by_category" }
        : {}),
      is_custom_rank: !!user?.is_custom_rank,
      ...values.personaSettings,
    };

    try {
      const response = await api.post(`${webSocketBaseURL}/categoryAnalysis`, payload);
      getHistory();
      const transactionId = response?.data?.transaction_id;
      const finalTransactionId = response?.data?.final_transaction_id;
      const totalCount = response?.data?.totalCount;
      const user_metric_setting_object = response?.data?.user_metric_setting_object ?? {};

      if (transactionId) {
        startPolling(transactionId, "brand");
        caCtx.updateStatus({ totalCount, completedTaskCount: 0 });
        caCtx.updateLaunchRecord({ transactionId });
        caCtx.setMetricSettingObj(user_metric_setting_object);
        caCtx.updateTransactionStatus("RUNNING");
      }
      if (showLaunchMessage !== "true" && launchType === "final") {
        setShowLaunchDialog(true);
      }
      caCtx.updateTransactionId(transactionId);
      caCtx.updateFinalTransactionId(finalTransactionId);
      caCtx.updateId(transactionId);
    } catch (error) {
      console.error("categoryAnalysisCall error:", error);
      toast.error("Failed to launch Category Analysis. Please try again.");
    }
  };

  const entity = caCtx.entity;
  const competitors = caCtx.competitors;

  return (
    <>
      {externalReportLoading && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1300,
            background: "rgba(255, 255, 255, 0.72)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 12,
          }}
        >
          <LinearProgress style={{ position: "fixed", top: 0, left: 0, right: 0 }} />
          <CircularProgress size={36} />
          <span style={{ fontSize: 13, fontWeight: 600, color: "#4a5875" }}>Loading category analysis…</span>
        </div>
      )}
      <section>
        {launchText && <Header actionText={launchText} hoverText={hoverText} actionVariant="text" />}
        <div className={classes.page_wrapper}>
          <div className={classes.container}>
            <div className={classes.content}>
              <CAForm
                warningText={warningText}
                setWarningText={setWarningText}
                cancelTransaction={cancelTransaction}
                setCancelTransaction={setCancelTransaction}
                stopPolling={stopPolling}
                startPolling={startPolling}
                setLaunchType={setLaunchType}
                launchType={launchType}
                isAuditRunning={isAuditRunning}
                setIsAuditRunning={setIsAuditRunning}
                loadCategoryAnalysis={loadCategoryAnalysis}
                setActiveTab={setActiveTab}
                processRetry={processRetry}
                caMode={caMode}
                onCAModeChange={setCaMode}
                isBaselineCategoryAnalysisEnabled={isBaselineCategoryAnalysisEnabled}
                onBaselineCAHtmlReportChange={setIsBaselineCAHtmlReportEnabled}
                onReset={() => {
                  setCaMode(isBaselineCategoryAnalysisEnabled ? "baseline" : "full");
                  setIsBaselineCAHtmlReportEnabled(false);
                  setWarningText("");
                }}
                onStartNew={() => {
                  setWarningText("");
                }}
                onSubmit={(values) => {
                  if (launchesRemaining === 0 && !isSpecial) {
                    toast.warn(`You have reached your ${launchLimitFrequency} limit`);
                    return;
                  }
                  if (!isSpecial) {
                    updateLaunches(values.personaMap);
                  } else {
                    updatePersonasOnly(values.personaMap);
                  }

                  // The first category serves as the "entity" label
                  const firstCategory = values.category[0] || "";
                  const newCompetitors = { ...values.competitorsData };

                  caCtx.refreshState();
                  caCtx.updateEntity(firstCategory);
                  caCtx.updatedCompetitors(newCompetitors);
                  caCtx.updatePersonaSettings(values.personaSettings);
                  caCtx.updateRbiType(values.rbiType || "propensityToRecommend");
                  caCtx.updateKeywords(values.keywords);
                  caCtx.updateSelectedMetrics(values.selectedMetrics);
                  caCtx.updateCustomTemplateID(values.metricSettingId === "default" ? null : values.metricSettingId);
                  caCtx.updateSelectedLLM(values.primaryLLM);
                  caCtx.updatePrimaryLLM(values.primaryLLM);
                  caCtx.updateMarket(values.market.length > 0 ? values.market : ["General Market"]);
                  caCtx.updatePersona(values.persona.length > 0 ? values.persona : [{ title: "General Persona", description: "General Persona" }]);
                  caCtx.updateCategory(values.category);
                  caCtx.updateLLM(values.llm);
                  caCtx.updateSelectedMarket(values.market.length > 0 ? values.market[0] : "General Market");
                  caCtx.updateSelectedCategory(values.category[0]);
                  caCtx.updateSelectedPersona(values.persona[0]?.title ?? values.persona[0]);
                  caCtx.updateType("brand");
                  caCtx.updateName(`${firstCategory} Category Analysis Overview`);

                  const launchRecord = caCtx.state.launchRecord;
                  const totalTestAudit = launchRecord?.length > 0 ? launchRecord[launchRecord.length - 1]?.totalTestAudit ?? 0 : 0;
                  const finalLaunchName = launchType === "final" ? "Final Analysis" : `Test Analysis ${totalTestAudit + 1}`;
                  setActiveTab(launchRecord?.length);
                  caCtx.updateLaunchRecord(
                    launchType === "final"
                      ? { name: finalLaunchName }
                      : { name: finalLaunchName, totalTestAudit: totalTestAudit + 1 }
                  );
                  caCtx.updateConversationEnabled(false);

                  setTimeout(() => {
                    caCtx.updatedLaunched(true);
                    caCtx.updateLaunchTransaction(true);
                    caCtx.updateLoading(true);
                    setIsAuditRunning(true);
                    setTimeout(() => { categoryAnalysisCall(values); }, 500);
                  }, 500);
                }}
              />
            </div>
          </div>

          {caCtx.is_launched && (
            <div className={classes.result} id="print-pdf" ref={pdfRef}>
              {/* Screen-only content: retry banners, launch tabs, progress, dashboard */}
              <div className="no-print">
                {(() => {
                  const { transactionStatus } = caCtx.state;
                  if (transactionStatus === "RETRY") {
                    return (
                      <div className={classes.failMessage}>
                        <div>
                          <span style={{ fontSize: "18px", marginRight: "8px" }}>⚠️</span>
                          Some requests failed. Click Retry to try again.
                        </div>
                        <MUIButton onClick={retryCategoryAnalysis} loading={processRetry}>Retry</MUIButton>
                      </div>
                    );
                  }
                  if (transactionStatus === "RUNNING" && processRetry) {
                    return (
                      <div className={classes.retryMessage}>
                        <div>
                          <span style={{ fontSize: "18px", marginRight: "8px" }}>⏳</span>
                          Retry process initiated.
                        </div>
                        <MUIButton onClick={retryCategoryAnalysis} loading={processRetry}>Retry</MUIButton>
                      </div>
                    );
                  }
                  return null;
                })()}

                {/* Launch tabs (Test Overview 1, Test Overview 2, Final Overview) */}
                <div className={classes.launchTabsWrapper}>
                  <MUITabs
                    value={activeTab}
                    onChange={handleTabChange}
                    indicatorColor="primary"
                    textColor="primary"
                    variant="scrollable"
                    scrollButtons="auto"
                  >
                    {caCtx?.state?.launchRecord?.map((item, index) => (
                      <Tab key={index} label={item.name} />
                    ))}
                  </MUITabs>
                </div>

                {caCtx?.state?.status?.totalCount > 0 && (
                  <div style={{ margin: "4px 0" }}>
                    <ProgressIndicator
                      total={caCtx.state.status.totalCount}
                      completed={caCtx.state.status.completedTaskCount}
                    />
                  </div>
                )}

                {/* Dashboard: single active category tab */}
                <CategoryDashboard
                  categories={caCtx.category}
                  market={caCtx?.markets?.[0]}
                  llms={caCtx.llm}
                  competitors={competitors}
                  enableHTMLReport={enableHTMLReport && caCtx.state.transactionStatus === "COMPLETED"}
                  transactionId={caCtx.state.transactionId}
                  brandName={caCtx.entity || caCtx.category?.[0] || ""}
                  markets={caCtx.markets || []}
                  personas={
                    caCtx?.persona?.map((item) =>
                      typeof item === "string" ? item : item?.title
                    )?.filter(Boolean) || []
                  }
                />
              </div>

              {/* Print-only content: all category sections rendered in sequence */}
              <div className="ca-print-only" style={{ display: "none" }}>
                <CAPDFContent
                  categories={caCtx.category}
                  competitors={competitors}
                />
              </div>

              {/* Export button — visible on screen only when analysis is complete */}
              {caCtx.state.transactionStatus === "COMPLETED" && (
                <div
                  className="no-print"
                  style={{ display: "flex", justifyContent: "flex-end", padding: "12px 0" }}
                >
                  <MUIButton onClick={exportToPDF}>Export</MUIButton>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {showLaunchDialog && (
        <Modal
          handleConfirm={() => setShowLaunchDialog(false)}
          open={showLaunchDialog}
          title=""
          showActions
          showLaunchDialog
        >
          <div className={classes.contentText}>
            The Category Analysis report is currently in-progress. This process typically takes 2 to 3 hours.
            You may stay on this screen or return later to view the completed report in the History section.
          </div>
        </Modal>
      )}

      <Modal
        title="Template Setting"
        width="1200"
        children={userMetricSettingCreation()}
        open={showUserMetricSettingModel}
        handleClose={() => dispatch({ type: "ACTION_MODEL", payload: false })}
        confirmBtnText="Add"
      />
    </>
  );
}

export default CategoryAnalysisInner;
